import React, { useEffect, useMemo, useCallback, useRef } from "react";
import { JsonForms } from "@jsonforms/react";
import { materialRenderers } from "../jsonForms/materialRenderers.jsx";
import { materialCells } from "@jsonforms/material-renderers";
import { Box, Divider, Typography, Grid, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  useCurrentAppointment,
  useCurrentAppointmentRequiresRtg,
  useWizardContext,
} from "./AppointmentWizardContext.jsx";
import isEqual from "lodash/isEqual";
import { assocIn } from "shared/src/util.mjs";
import { appointmentStep } from "shared/src/appointment.mjs";

function extractLabelMap(ui) {
  const map = new Map();

  const walk = (node) => {
    if (!node || typeof node !== "object") return;
    if (Array.isArray(node.elements)) node.elements.forEach(walk);

    if (node.type === "Control" && typeof node.scope === "string") {
      const m = node.scope.match(/#\/properties\/([^/]+)/);
      const key = m?.[1];
      if (key) {
        const label =
          (typeof node.label === "string" && node.label) ||
          (typeof node.label === "object" && node.label?.text) ||
          key;
        map.set(key, label);
      }
    }
  };

  walk(ui);
  return map;
}

const cleanFormData = (data, schema) => {
  const allowed = Object.keys(JSON.parse(schema || "{}")?.properties || {});
  return Object.fromEntries(
    Object.entries(data || {}).filter(([k]) => allowed.includes(k)),
  );
};

export function toStructuredFields({ cleanedData, jsonSchema, uiSchema }) {
  const schemaObj = JSON.parse(jsonSchema || "{}");
  const props = schemaObj.properties || {};
  const labelMap = extractLabelMap(JSON.parse(uiSchema || "{}"));

  return Object.entries(cleanedData).map(([key, value]) => {
    const def = props[key] || {};
    const isMedicalDocumentation =
      String(def.medicalDocumentation || "").toLowerCase() === "yes";
    const label = labelMap.get(key) || key;

    return {
      key,
      label,
      value,
      isMedicalDocumentation,
    };
  });
}

function parseJsonObject(s) {
  try {
    return JSON.parse(s ?? "{}");
  } catch {
    return {};
  }
}

/**
 * @param {object} uischema
 * @param {Set<string>} scopes
 *
 * @returns {object}
 */
function readonlyScopes(uischema, scopes) {
  const elements = uischema.elements;
  if (elements == null) {
    return scopes.has(uischema.scope)
      ? assocIn(uischema, ["options", "readonly"], true)
      : uischema;
  }

  return {
    ...uischema,
    elements: elements.map((e) => readonlyScopes(e, scopes)),
  };
}

function readonlyProperties(uischema, properties) {
  if (properties.length === 0) {
    return uischema;
  }

  return readonlyScopes(
    uischema,
    new Set(properties.map((p) => `#/properties/${p}`)),
  );
}

function useJsonFormProps(service) {
  const { filledFormsData, rtgCreated } = useWizardContext();
  const currentAppointment = useCurrentAppointment();
  const currentAppointmentRequiresRtg = useCurrentAppointmentRequiresRtg();
  const prefillSkippedRtg =
    currentAppointmentRequiresRtg &&
    !rtgCreated &&
    currentAppointment?.active_step === appointmentStep.PROCEDURE_NOTES;

  const serviceTemplate = service?.templates?.[0];
  const template = serviceTemplate?.template;

  return React.useMemo(() => {
    if (template == null) {
      return;
    }

    const readonlyData = prefillSkippedRtg
      ? { teleWillBeTaken: "No", opgWillBeTaken: "No" }
      : {};

    const data =
      filledFormsData?.[serviceTemplate.id]?.fields?.reduce((acc, f) => {
        acc[f.key] = f.value;
        return acc;
      }, {}) ?? {};

    const uischema = parseJsonObject(serviceTemplate.template?.uischema);

    return {
      schema: parseJsonObject(serviceTemplate.template?.schema),
      uischema: readonlyProperties(uischema, Object.keys(readonlyData)),
      data: { ...data, ...readonlyData },
      renderers: materialRenderers,
      cells: materialCells,
      validationMode: "ValidateAndShow",
    };
  }, [serviceTemplate, filledFormsData, prefillSkippedRtg]);
}

const ServiceJsonFormComponent = React.memo(
  ({ service, translation, formSubmitted, onFormChange }) => {
    const { filledFormsData, setIsTemplateFormValid } = useWizardContext();

    const templateId = service?.templates?.[0]?.id;
    const template = service?.templates?.[0]?.template;

    const jsonFormProps = useJsonFormProps(service);

    const lastSentDataRef = useRef(null);

    useEffect(() => {
      if (!templateId || !template) {
        setIsTemplateFormValid(true);
        return;
      }

      const saved = filledFormsData?.[templateId];
      const current = filledFormsData?.[templateId];

      if (!isEqual(saved, current)) {
        lastSentDataRef.current = saved;
        if (saved) {
          onFormChange(templateId, saved);
        }
      } else {
        lastSentDataRef.current = current;
      }
    }, [
      templateId,
      template,
      filledFormsData,
      onFormChange,
      setIsTemplateFormValid,
    ]);

    const handleFormChange = useCallback(
      (data, errors) => {
        const jsonSchema = template?.schema;
        const uiSchema = template?.uischema;

        const cleaned =
          jsonSchema && typeof jsonSchema === "string"
            ? cleanFormData(data, jsonSchema)
            : data;

        if (!templateId) {
          setIsTemplateFormValid(true);
          return;
        }

        setIsTemplateFormValid(errors.length === 0);

        const structured = {
          service: {
            id: service?.id || null,
            name: service?.name || null,
            version: service?.templates?.[0]?.version || null,
          },
          fields: toStructuredFields({
            cleanedData: cleaned,
            jsonSchema,
            uiSchema,
          }),
        };

        if (isEqual(structured, lastSentDataRef.current)) {
          return;
        }

        lastSentDataRef.current = structured;
        onFormChange(templateId, structured);
      },
      [template, templateId, onFormChange, setIsTemplateFormValid, service],
    );

    return jsonFormProps != null ? (
      <JsonForms
        {...jsonFormProps}
        onChange={({ data, errors }) => handleFormChange(data, errors)}
        config={{ forceShow: formSubmitted }}
        i18n={{ translate: translation }}
      />
    ) : (
      <div>{translation("service.notFound", "Service not found")}</div>
    );
  },
);

ServiceJsonFormComponent.displayName = "ServiceJsonFormComponent";

export default function AppointmentServices({
  service,
  formSubmitted,
  onFormChange,
}) {
  const { t, i18n } = useTranslation(["translation", "serviceTemplates"]);

  const translation = useMemo(
    () => (key, defaultMessage) =>
      i18n.exists(`serviceTemplates:${key}`)
        ? t(`serviceTemplates:${key}`)
        : defaultMessage,
    [i18n.language],
  );

  return (
    <>
      <Typography variant="h6" sx={{ marginBottom: 2 }}>
        {t("appointmentNotes.serviceNotes")}
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              label={t("appointmentNotes.service")}
              value={service?.name ?? ""}
              fullWidth
              disabled
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 2 }}>
          <ServiceJsonFormComponent
            service={service}
            formSubmitted={formSubmitted}
            translation={translation}
            onFormChange={onFormChange}
          />
        </Box>

        <Divider sx={{ marginTop: 4, marginBottom: 4 }} />
      </Box>
    </>
  );
}
